import { InterestType, } from './assets.js';
export const EXPECTED_OUTSTANDING_DEFAULTS = {
    totalTermYears: 30,
    interestOnlyYearsLow: 0,
    interestOnlyYearsHigh: 10,
    variableRateMarginPct: 2.5,
};
export const ATTACHMENT_OUTSTANDING_DEFAULTS = {
    zeroRateLowPct: 0,
    zeroRateHighPct: 20,
    accrualYears: 1,
};
// Annual contribution rate (bidragssats) by interest type and LTV bucket,
// in percent. Edit these numbers to tune the assumptions.
export const CONTRIBUTION_RATE_TABLE_PCT = {
    [InterestType.Fixed]: {
        upToLtv40: 0.2,
        upToLtv60: 0.6,
        aboveLtv60: 1.0,
    },
    [InterestType.Variable]: {
        upToLtv40: 0.5,
        upToLtv60: 1.0,
        aboveLtv60: 1.5,
    },
};
export const estimateContributionRatePct = (interestType, ltvFraction) => {
    const row = CONTRIBUTION_RATE_TABLE_PCT[interestType];
    if (ltvFraction <= 0.4) {
        return row.upToLtv40;
    }
    if (ltvFraction <= 0.6) {
        return row.upToLtv60;
    }
    return row.aboveLtv60;
};
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
const yearsBetween = (from, to) => (to.getTime() - from.getTime()) / MS_PER_YEAR;
// Remaining balance of an annuity loan after a given amount of time, with an
// interest-only phase up front:
//   B = P * ((1+r)^n - (1+r)^p) / ((1+r)^n - 1)
// where p = max(0, t - I), n = T - I.
// All times are in years and r is an annual fraction (e.g. 0.045).
export const remainingAnnuityBalance = (params) => {
    const { principal } = params;
    const r = params.annualRate;
    const T = params.totalTermYears;
    const I = Math.max(0, params.interestOnlyYears);
    const t = Math.max(0, params.yearsElapsed);
    const n = T - I;
    if (n <= 0) {
        return 0;
    }
    const p = Math.max(0, t - I);
    if (p >= n) {
        return 0;
    }
    if (r === 0) {
        return principal * ((n - p) / n);
    }
    const onePlusR = 1 + r;
    const numerator = Math.pow(onePlusR, n) - Math.pow(onePlusR, p);
    const denominator = Math.pow(onePlusR, n) - 1;
    return principal * (numerator / denominator);
};
export const latestOutstandingBalance = (lien) => {
    const balances = lien.outstandingBalances ?? [];
    if (balances.length === 0) {
        return null;
    }
    let latest = balances[0];
    for (let i = 1; i < balances.length; i++) {
        if (balances[i].date >= latest.date) {
            latest = balances[i];
        }
    }
    return latest;
};
export const estimateExpectedOutstanding = (input) => {
    const { lien } = input;
    const asOfDate = input.asOfDate ?? new Date();
    // Step 1 — pick the principal reference point.
    const latestBalance = latestOutstandingBalance(lien);
    let principal;
    let referenceDateStr;
    if (latestBalance != null) {
        principal = latestBalance.amount;
        referenceDateStr = latestBalance.date;
    }
    else if (lien.amount != null) {
        principal = lien.amount;
        referenceDateStr = lien.registrationDate;
    }
    else {
        return null;
    }
    if (referenceDateStr == null) {
        return null;
    }
    const referenceDate = new Date(referenceDateStr);
    if (Number.isNaN(referenceDate.getTime())) {
        return null;
    }
    // Step 2 — years between loan registration and the principal reference date.
    // Used to translate original IO/term into IO/term remaining at the reference.
    const registrationDate = lien.registrationDate != null ? new Date(lien.registrationDate) : null;
    const yearsSinceStartAtReference = registrationDate != null && !Number.isNaN(registrationDate.getTime())
        ? Math.max(0, yearsBetween(registrationDate, referenceDate))
        : 0;
    // Step 3 — total term remaining at reference.
    const originalTotalTermYears = lien.totalTermYears ?? EXPECTED_OUTSTANDING_DEFAULTS.totalTermYears;
    const totalTermAssumed = lien.totalTermYears == null ? originalTotalTermYears : null;
    const T_remaining = originalTotalTermYears - yearsSinceStartAtReference;
    // Step 4 — IO years remaining at reference (range when missing).
    let ioRemainingLow;
    let ioRemainingHigh;
    let ioLowAssumed;
    let ioHighAssumed;
    if (lien.interestOnlyYears != null) {
        const remaining = Math.max(0, lien.interestOnlyYears - yearsSinceStartAtReference);
        ioRemainingLow = remaining;
        ioRemainingHigh = remaining;
        ioLowAssumed = null;
        ioHighAssumed = null;
    }
    else {
        ioRemainingLow = Math.max(0, EXPECTED_OUTSTANDING_DEFAULTS.interestOnlyYearsLow -
            yearsSinceStartAtReference);
        ioRemainingHigh = Math.max(0, EXPECTED_OUTSTANDING_DEFAULTS.interestOnlyYearsHigh -
            yearsSinceStartAtReference);
        ioLowAssumed = EXPECTED_OUTSTANDING_DEFAULTS.interestOnlyYearsLow;
        ioHighAssumed = EXPECTED_OUTSTANDING_DEFAULTS.interestOnlyYearsHigh;
    }
    // Step 5 — contribution rate.
    let contributionRatePct;
    let contributionRateAssumed;
    if (lien.contributionRate != null) {
        contributionRatePct = lien.contributionRate;
        contributionRateAssumed = null;
    }
    else {
        if (lien.interestType == null || lien.amount == null) {
            return null;
        }
        const transactionPrice = input.latestTransaction?.totalPurchaseSum;
        if (transactionPrice == null || transactionPrice <= 0) {
            return null;
        }
        const ltvNumerator = input.mortgageTotalForLtv ?? lien.amount;
        const ltvFraction = ltvNumerator / transactionPrice;
        contributionRatePct = estimateContributionRatePct(lien.interestType, ltvFraction);
        contributionRateAssumed = contributionRatePct;
    }
    // Step 6 — interest rate range.
    if (lien.interestRate == null) {
        return null;
    }
    const interestRatesPct = lien.interestType === InterestType.Variable
        ? [
            Math.max(0, lien.interestRate -
                EXPECTED_OUTSTANDING_DEFAULTS.variableRateMarginPct),
            lien.interestRate +
                EXPECTED_OUTSTANDING_DEFAULTS.variableRateMarginPct,
        ]
        : [lien.interestRate];
    const assumptions = {
        contributionRatePct: contributionRateAssumed,
        totalTermYears: totalTermAssumed,
        interestOnlyYearsLow: ioLowAssumed,
        interestOnlyYearsHigh: ioHighAssumed,
        interestRatePctLow: null,
        interestRatePctHigh: null,
    };
    // Term has already fully run down at the reference date.
    if (T_remaining <= 0) {
        return {
            low: 0,
            high: 0,
            currency: lien.currency,
            isFullyAssumed: false,
            assumptions,
        };
    }
    // Step 7 — t since the principal reference date.
    const t = Math.max(0, yearsBetween(referenceDate, asOfDate));
    // Step 8 — corner-evaluate B for every (rate, IO years) combination.
    const ioYearValues = ioRemainingLow === ioRemainingHigh
        ? [ioRemainingLow]
        : [ioRemainingLow, ioRemainingHigh];
    const balances = [];
    for (const ratePct of interestRatesPct) {
        const r = (ratePct + contributionRatePct) / 100;
        for (const I of ioYearValues) {
            balances.push(remainingAnnuityBalance({
                principal,
                annualRate: r,
                totalTermYears: T_remaining,
                interestOnlyYears: I,
                yearsElapsed: t,
            }));
        }
    }
    return {
        low: Math.min(...balances),
        high: Math.max(...balances),
        currency: lien.currency,
        isFullyAssumed: false,
        assumptions,
    };
};
export const estimateAttachmentOutstanding = (input) => {
    const { lien } = input;
    const asOfDate = input.asOfDate ?? new Date();
    const latestBalance = latestOutstandingBalance(lien);
    const principal = latestBalance?.amount ?? lien.amount;
    if (principal == null) {
        return null;
    }
    // Without a registration date the accrual is unknown, so there is no estimate
    const registrationDate = lien.registrationDate != null ? new Date(lien.registrationDate) : null;
    if (registrationDate == null || Number.isNaN(registrationDate.getTime())) {
        return null;
    }
    const accrualYears = Math.min(ATTACHMENT_OUTSTANDING_DEFAULTS.accrualYears, Math.max(0, yearsBetween(registrationDate, asOfDate)));
    let ratePctLow;
    let ratePctHigh;
    let assumedRateLow;
    let assumedRateHigh;
    if (lien.interestRate == null) {
        ratePctLow = ATTACHMENT_OUTSTANDING_DEFAULTS.zeroRateLowPct;
        ratePctHigh = ATTACHMENT_OUTSTANDING_DEFAULTS.zeroRateHighPct;
        assumedRateLow = ratePctLow;
        assumedRateHigh = ratePctHigh;
    }
    else {
        ratePctLow = lien.interestRate;
        ratePctHigh = lien.interestRate;
        assumedRateLow = null;
        assumedRateHigh = null;
    }
    return {
        low: principal * (1 + (ratePctLow / 100) * accrualYears),
        high: principal * (1 + (ratePctHigh / 100) * accrualYears),
        currency: lien.currency,
        isFullyAssumed: true,
        assumptions: {
            contributionRatePct: null,
            totalTermYears: null,
            interestOnlyYearsLow: null,
            interestOnlyYearsHigh: null,
            interestRatePctLow: assumedRateLow,
            interestRatePctHigh: assumedRateHigh,
        },
    };
};
//# sourceMappingURL=lien-outstanding.js.map